import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmModal from '../Common/ConfirmModal';
import { RecordFormModal, RecordDetailsModal } from '../Common/admin';
import { buildRoots } from './treeNodes';
import PropertyIntakeModal from '../Property/PropertyIntakeModal';
import PropertyLedgerModal from '../Property/PropertyLedgerModal';
import ContractIntakeModal from '../Contract/ContractIntakeModal';
import OwnerProfileModal from '../Owner/OwnerProfileModal';
import FinancialReportModal from '../Reports/FinancialReportModal';
import OfficeSettingsModal from '../Settings/OfficeSettingsModal';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Finder-style column browser: selecting an item opens the next column beside
 * it. Add/edit/delete/details all happen in dialogs — nothing navigates away.
 */
const TreeBrowser = () => {
  const { t } = useTranslation();
  const { can, isOwner, isSuperAdmin, isOrgAdmin } = useAuth();
  const roots = useMemo(() => buildRoots(t, can), [t, can]);
  const scrollerRef = useRef(null);

  const [columns, setColumns] = useState([
    { nodes: roots, loading: false, error: null, selectedId: null, parent: null, search: '' },
  ]);

  // Dialog state: only one is ever open.
  const [details, setDetails] = useState(null);
  const [form, setForm] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [intakeColumn, setIntakeColumn] = useState(null);
  // { propertyId, mode, columnIndex } — details/edit of an existing property.
  const [intakeRecord, setIntakeRecord] = useState(null);
  const [ledgerProperty, setLedgerProperty] = useState(null);
  // { propertyId, columnIndex } — the contract intake dialog.
  const [contractIntake, setContractIntake] = useState(null);
  // { contractId, mode, columnIndex } — details/edit of an existing contract.
  const [contractRecord, setContractRecord] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [reportProperty, setReportProperty] = useState(null);
  const [showAllReports, setShowAllReports] = useState(false);
  const [showOfficeSettings, setShowOfficeSettings] = useState(false);

  useEffect(() => {
    setColumns((prev) => {
      const next = [...prev];
      next[0] = { ...next[0], nodes: roots };
      return next;
    });
  }, [roots]);

  /** Fill `targetIndex` with the children of `node`. */
  const loadInto = useCallback(async (targetIndex, node, search = '') => {
    if (!node?.loadChildren) return;

    setColumns((prev) => {
      const next = [...prev];
      next[targetIndex] = {
        nodes: [],
        loading: true,
        error: null,
        selectedId: null,
        parent: node,
        search,
      };
      return next.slice(0, targetIndex + 1);
    });

    try {
      const children = await node.loadChildren(search);
      setColumns((prev) => {
        const next = [...prev];
        // Ignore a response whose column has since been replaced.
        if (next[targetIndex]?.parent?.id !== node.id) return prev;
        next[targetIndex] = { ...next[targetIndex], nodes: children, loading: false };
        return next;
      });
    } catch (err) {
      console.error('Tree load failed:', err);
      const status = err.response?.status;
      const detail = apiErrorMessage(err);
      setColumns((prev) => {
        const next = [...prev];
        if (next[targetIndex]?.parent?.id !== node.id) return prev;
        next[targetIndex] = {
          ...next[targetIndex],
          loading: false,
          error: status ? `${status} — ${detail}` : detail,
        };
        return next;
      });
    }
  }, []);

  const openNode = useCallback(
    (columnIndex, node) => {
      // Some entries are a dialog rather than another column; opening one must
      // not disturb the columns already on screen.
      if (node.opens === 'ledger') {
        setLedgerProperty(node.target);
        return;
      }
      if (node.opens === 'report') {
        setReportProperty(node.target);
        return;
      }

      setColumns((prev) => {
        const kept = prev.slice(0, columnIndex + 1);
        kept[columnIndex] = { ...kept[columnIndex], selectedId: node.id };
        return kept;
      });
      loadInto(columnIndex + 1, node);
    },
    [loadInto]
  );

  /** Re-fetch a column in place — used after a record changes. */
  const refresh = useCallback(
    (columnIndex) => {
      const column = columns[columnIndex];
      if (column?.parent) loadInto(columnIndex, column.parent, column.search);
    },
    [columns, loadInto]
  );

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [columns.length]);

  const submitForm = async (values) => {
    await form.submit(values);
    const index = form.columnIndex;
    setForm(null);
    refresh(index);
  };

  const confirmDelete = async () => {
    const { run, columnIndex } = pendingDelete;
    setPendingDelete(null);
    try {
      await run();
      refresh(columnIndex);
    } catch (err) {
      console.error('Delete failed:', err);
      setColumns((prev) => {
        const next = [...prev];
        if (!next[columnIndex]) return prev;
        next[columnIndex] = {
          ...next[columnIndex],
          error: apiErrorMessage(err),
        };
        return next;
      });
    }
  };

  return (
    <div className="ad-page">
      <div className="ad-page-head ad-page-head-bare">
        <div />
        <div className="ad-toolbar-group">
          <button type="button" className="ad-btn" onClick={() => setShowAllReports(true)}>
            <i className="fas fa-chart-pie"></i>
            {t('reports.summaryTitle')}
          </button>
          {isOwner && (
            <button type="button" className="ad-btn" onClick={() => setShowProfile(true)}>
              <i className="fas fa-user-gear"></i>
              {t('owner.profile.title')}
            </button>
          )}
          {(isSuperAdmin || isOrgAdmin) && (
            <button type="button" className="ad-btn" onClick={() => setShowOfficeSettings(true)}>
              <i className="fas fa-sliders"></i>
              {t('settings.title')}
            </button>
          )}
        </div>
      </div>

      <div className="ad-columns" ref={scrollerRef}>
        {columns.map((column, index) => (
          <TreeColumn
            key={column.parent ? `${column.parent.id}-${index}` : 'roots'}
            column={column}
            onSelect={(node) => openNode(index, node)}
            onSearch={(term) => loadInto(index, column.parent, term)}
            onAdd={() =>
              column.parent.createIntake
                ? setIntakeColumn(index)
                : column.parent.createContract
                ? setContractIntake({
                    propertyId: column.parent.createContract.propertyId || null,
                    columnIndex: index,
                  })
                : setForm({
                title: column.parent.create.title,
                fields: column.parent.create.fields,
                initial: null,
                submit: column.parent.create.submit,
                columnIndex: index,
                  })
            }
            onDetails={(node) => {
              if (node.intake) {
                setIntakeRecord({ propertyId: node.intake.id, mode: 'view', columnIndex: index });
              } else if (node.contractIntake) {
                setContractRecord({
                  contractId: node.contractIntake.id,
                  mode: 'view',
                  columnIndex: index,
                });
              } else {
                setDetails(node.details);
              }
            }}
            onLedger={(node) => setLedgerProperty(node.ledger)}
            onReport={(node) => setReportProperty(node.report)}
            onEdit={(node, target = index) =>
              node.intake
                ? setIntakeRecord({
                    propertyId: node.intake.id,
                    mode: 'edit',
                    columnIndex: target,
                  })
                : node.contractIntake
                ? setContractRecord({
                    contractId: node.contractIntake.id,
                    mode: 'edit',
                    columnIndex: target,
                  })
                : setForm({
                title: node.edit.title,
                fields: node.edit.fields,
                initial: node.edit.initial,
                submit: node.edit.submit,
                columnIndex: target,
              })
            }
            onDelete={(node, target = index) =>
              setPendingDelete({ run: node.remove, columnIndex: target })
            }
            // Acting on the header edits the node that opened this column, so
            // the list to refresh is the one before it.
            parentColumnIndex={index - 1}
          />
        ))}
      </div>

      <RecordDetailsModal
        show={Boolean(details)}
        title={details?.title}
        items={details?.items || []}
        onClose={() => setDetails(null)}
      />

      <RecordFormModal
        show={Boolean(form)}
        title={form?.title}
        fields={form?.fields || []}
        initial={form?.initial}
        onSubmit={submitForm}
        onClose={() => setForm(null)}
      />

      <PropertyIntakeModal
        show={intakeColumn !== null}
        onClose={() => setIntakeColumn(null)}
        onSaved={() => refresh(intakeColumn)}
      />

      <PropertyIntakeModal
        show={intakeRecord !== null}
        propertyId={intakeRecord?.propertyId}
        mode={intakeRecord?.mode}
        onClose={() => setIntakeRecord(null)}
        onSaved={() => refresh(intakeRecord?.columnIndex)}
      />

      <OwnerProfileModal show={showProfile} onClose={() => setShowProfile(false)} />

      <OfficeSettingsModal
        show={showOfficeSettings}
        onClose={() => setShowOfficeSettings(false)}
      />

      <FinancialReportModal
        show={reportProperty !== null}
        property={reportProperty}
        scope="property"
        onClose={() => setReportProperty(null)}
      />

      <FinancialReportModal
        show={showAllReports}
        scope="summary"
        onClose={() => setShowAllReports(false)}
      />

      <ContractIntakeModal
        show={contractIntake !== null}
        propertyId={contractIntake?.propertyId}
        onClose={() => setContractIntake(null)}
        onSaved={() => refresh(contractIntake?.columnIndex)}
      />

      <ContractIntakeModal
        show={contractRecord !== null}
        contractId={contractRecord?.contractId}
        mode={contractRecord?.mode}
        onClose={() => setContractRecord(null)}
        onSaved={() => refresh(contractRecord?.columnIndex)}
      />

      <PropertyLedgerModal
        show={ledgerProperty !== null}
        property={ledgerProperty}
        onClose={() => setLedgerProperty(null)}
      />

      <ConfirmModal
        show={Boolean(pendingDelete)}
        title={t('common.confirm')}
        message={t('tree.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

/** Details / edit / delete buttons, used both in a column header and on leaf rows. */
const NodeActions = ({ node, onDetails, onEdit, onDelete, onLedger, onReport, className = '' }) => {
  const { t } = useTranslation();
  if (!node.details && !node.intake && !node.contractIntake && !node.edit && !node.remove && !node.to)
    return null;

  return (
    <div className={className}>
      {(node.details || node.intake || node.contractIntake) && (
        <button
          type="button"
          className="ad-node-action"
          onClick={() => onDetails(node)}
          title={t('tree.details')}
        >
          <i className="fas fa-circle-info"></i>
        </button>
      )}
      {node.report && onReport && (
        <button
          type="button"
          className="ad-node-action"
          onClick={() => onReport(node)}
          title={t('reports.propertyTitle')}
        >
          <i className="fas fa-chart-pie"></i>
        </button>
      )}
      {node.ledger && onLedger && (
        <button
          type="button"
          className="ad-node-action"
          onClick={() => onLedger(node)}
          title={t('property.ledger.title')}
        >
          <i className="fas fa-table-list"></i>
        </button>
      )}
      {(node.edit || node.intake || node.contractIntake) && (
        <button
          type="button"
          className="ad-node-action"
          onClick={() => onEdit(node)}
          title={t('tree.edit')}
        >
          <i className="fas fa-pen"></i>
        </button>
      )}
      {node.remove && (
        <button
          type="button"
          className="ad-node-action ad-node-action-danger"
          onClick={() => onDelete(node)}
          title={t('tree.delete')}
        >
          <i className="fas fa-trash"></i>
        </button>
      )}
      {node.to && (
        <Link to={node.to} className="ad-node-action" title={t('tree.openPage')}>
          <i className="fas fa-arrow-up-right-from-square"></i>
        </Link>
      )}
    </div>
  );
};

const TreeColumn = ({
  column,
  onSelect,
  onSearch,
  onAdd,
  onDetails,
  onEdit,
  onDelete,
  onLedger,
  onReport,
  parentColumnIndex,
}) => {
  const { t, i18n } = useTranslation();
  const [term, setTerm] = useState(column.search || '');
  const searchable = Boolean(column.parent?.searchKey);
  const addable = Boolean(
    column.parent?.create || column.parent?.createIntake || column.parent?.createContract
  );
  // This page disables CSS transforms, so pick the arrow that already points
  // toward the next column instead of flipping one.
  const arrow = i18n.dir() === 'rtl' ? 'fa-chevron-left' : 'fa-chevron-right';

  return (
    <div className="ad-column">
      {column.parent && (
        <div className="ad-column-head">
          <i className={`fas ${column.parent.icon}`}></i>
          <span className="ad-column-head-title">{column.parent.label}</span>

          {/* Actions for the item that opened this column. */}
          <NodeActions
            node={column.parent}
            className="ad-column-head-actions"
            onDetails={onDetails}
            onEdit={(node) => onEdit(node, parentColumnIndex)}
            onDelete={(node) => onDelete(node, parentColumnIndex)}
            onLedger={onLedger}
            onReport={onReport}
          />

          {addable && (
            <button
              type="button"
              className="ad-column-add"
              onClick={onAdd}
              title={t('tree.add')}
            >
              <i className="fas fa-plus"></i>
            </button>
          )}
        </div>
      )}

      {searchable && (
        <form
          className="ad-column-search"
          onSubmit={(e) => {
            e.preventDefault();
            onSearch(term);
          }}
        >
          <input
            type="text"
            value={term}
            placeholder={t(`tree.${column.parent.searchKey}`)}
            onChange={(e) => setTerm(e.target.value)}
          />
          <button type="submit" title={t('common.search')}>
            <i className="fas fa-search"></i>
          </button>
        </form>
      )}

      <div className="ad-column-body">
        {column.loading && <p className="ad-column-note">{t('tree.loading')}</p>}
        {column.error && (
          <p className="ad-column-note ad-column-error">
            {t('tree.error')}
            <span className="ad-column-error-detail">{column.error}</span>
          </p>
        )}
        {!column.loading && !column.error && column.nodes.length === 0 && (
          <p className="ad-column-note">{t('tree.empty')}</p>
        )}

        {!column.loading &&
          !column.error &&
          column.nodes.map((node) => (
            <div
              key={node.id}
              className={`ad-node ${column.selectedId === node.id ? 'ad-node-selected' : ''}`}
            >
              <button
                type="button"
                className="ad-node-main"
                onClick={() =>
                  node.loadChildren || node.opens ? onSelect(node) : onDetails(node)
                }
                disabled={!node.loadChildren && !node.opens && !node.details}
              >
                <i className={`fas ${node.icon} ad-node-icon`}></i>
                <span className="ad-node-text">
                  <span className="ad-node-label">{node.label}</span>
                  {node.sublabel && <span className="ad-node-sub">{node.sublabel}</span>}
                </span>
                {node.meta && <span className="ad-node-meta">{node.meta}</span>}
                {node.loadChildren && <i className={`fas ${arrow} ad-node-arrow`}></i>}
                {node.opens && (
                  <i className="fas fa-arrow-up-right-from-square ad-node-arrow"></i>
                )}
              </button>

              {/* Leaves never open a column, so their actions stay on the row;
                  anything expandable shows them in the header once opened. */}
              {!node.loadChildren && (
                <NodeActions
                  node={node}
                  className="ad-node-actions"
                  onDetails={onDetails}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onLedger={onLedger}
                  onReport={onReport}
                />
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default TreeBrowser;
